/**
 * CreateAgentDialog — modal pour créer une `app.agent_identities` (9.M.7).
 *
 * Backend : POST /v1/agent-identities (super_admin only via RLS).
 *
 * Champs :
 *   - kind (radio ai_operator | external_system) — requis
 *   - display_name (text 1..200) — requis
 *   - description (textarea 1..2000) — optionnel
 *   - avatar_url (text 1..500) — optionnel
 *   - tenant_id : pas Phase 1 (UI minimal → toujours global tenant_id=NULL).
 *     Si super_admin veut un agent scoped à un tenant, fait via SQL Editor
 *     (dette D30.2 — UI scope picker tenant).
 *
 * UX :
 *   - Inputs disabled pendant submit.
 *   - Esc / backdrop click → close (sauf pendant submit).
 *   - Focus initial sur display_name (anti-mis-click).
 *   - Toast success → close + redirect /settings/agent-identities/:id.
 */

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiException } from "../../../../lib/api";
import {
  getApiErrorMessage,
  normalizeLang,
} from "../../../../lib/apiErrorMessage";
import {
  useCreateAgentIdentity,
  type CreateAgentIdentityPayload,
} from "../../../../lib/queries/agentIdentities";
import { toast } from "../../../../lib/toast";

type Props = { readonly onClose: () => void };

const DISPLAY_MAX = 200;
const DESC_MAX = 2000;
const AVATAR_MAX = 500;

export function CreateAgentDialog({ onClose }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const navigate = useNavigate();
  const titleId = useId();
  const kindRadioName = useId();
  const displayId = useId();
  const descId = useId();
  const avatarId = useId();
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  const create = useCreateAgentIdentity();
  const { reset: resetMutation } = create;

  const [kind, setKind] = useState<"ai_operator" | "external_system">("ai_operator");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");

  useEffect(() => {
    resetMutation();
    queueMicrotask(() => firstFieldRef.current?.focus());
  }, [resetMutation]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !create.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, create.isPending]);

  const trimmedDisplay = displayName.trim();
  const trimmedDesc = description.trim();
  const trimmedAvatar = avatarUrl.trim();
  const canSubmit =
    trimmedDisplay.length >= 1 &&
    trimmedDisplay.length <= DISPLAY_MAX &&
    trimmedDesc.length <= DESC_MAX &&
    trimmedAvatar.length <= AVATAR_MAX &&
    !create.isPending;

  const onSubmit = () => {
    if (!canSubmit) return;
    const payload: CreateAgentIdentityPayload = {
      kind,
      display_name: trimmedDisplay,
      tenant_id: null, // Phase 1 UI minimal — toujours global. Dette D30.2.
    };
    if (trimmedDesc.length >= 1) payload.description = trimmedDesc;
    if (trimmedAvatar.length >= 1) payload.avatar_url = trimmedAvatar;

    create.mutate(payload, {
      onSuccess: (row) => {
        toast.success(t("agent_identities.create_dialog.success"));
        onClose();
        navigate(`/settings/agent-identities/${row.id}`);
      },
    });
  };

  const apiErrorMsg =
    create.error instanceof ApiException
      ? getApiErrorMessage(create.error, lang) || t("errors.generic")
      : create.isError
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!create.isPending) onClose();
        }}
      />
      <div className="relative z-10 w-full max-w-lg rounded-xl bg-white shadow-xl ring-1 ring-slate-200 motion-safe:animate-fade-in">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("agent_identities.create_dialog.title")}
          </h2>
        </header>

        <div className="space-y-4 px-5 py-5">
          <fieldset>
            <legend className="mb-2 block text-xs font-medium text-slate-700">
              {t("agent_identities.create_dialog.kind_label")}{" "}
              <span aria-hidden="true" className="text-red-600">
                *
              </span>
            </legend>
            <div className="flex flex-wrap gap-3">
              {(["ai_operator", "external_system"] as const).map((k) => (
                <label
                  key={k}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                >
                  <input
                    type="radio"
                    name={kindRadioName}
                    value={k}
                    checked={kind === k}
                    onChange={() => setKind(k)}
                    disabled={create.isPending}
                    className="h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  {t(`agent_identities.kinds.${k}`)}
                </label>
              ))}
            </div>
          </fieldset>

          <div>
            <label
              htmlFor={displayId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t("agent_identities.create_dialog.display_name_label")}{" "}
              <span aria-hidden="true" className="text-red-600">
                *
              </span>
            </label>
            <input
              ref={firstFieldRef}
              id={displayId}
              type="text"
              maxLength={DISPLAY_MAX}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={create.isPending}
              placeholder={t("agent_identities.create_dialog.display_name_placeholder")}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-slate-500">
              {trimmedDisplay.length}/{DISPLAY_MAX}
            </p>
          </div>

          <div>
            <label
              htmlFor={descId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t("agent_identities.create_dialog.description_label")}
            </label>
            <textarea
              id={descId}
              rows={3}
              maxLength={DESC_MAX}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={create.isPending}
              placeholder={t("agent_identities.create_dialog.description_placeholder")}
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-slate-500">
              {trimmedDesc.length}/{DESC_MAX}
            </p>
          </div>

          <div>
            <label
              htmlFor={avatarId}
              className="mb-1.5 block text-xs font-medium text-slate-700"
            >
              {t("agent_identities.create_dialog.avatar_url_label")}
            </label>
            <input
              id={avatarId}
              type="url"
              maxLength={AVATAR_MAX}
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              disabled={create.isPending}
              placeholder="https://…"
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
            />
          </div>

          <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {t("agent_identities.create_dialog.scope_hint")}
          </p>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={create.isPending}
            className="rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 touch-manipulation"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            aria-busy={create.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 touch-manipulation"
          >
            {create.isPending
              ? t("agent_identities.create_dialog.submitting")
              : t("agent_identities.create_dialog.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
